// Type definitions for enhanced TypeScript safety

export type Language = 'en' | 'ar';

export interface TranslationKeys {
  // Header
  about: string;
  services: string;
  vehicleRental: string;
  contact: string;
  getQuote: string;

  // Hero
  heroTitle: string;
  heroSubtitle: string;
  heroButton: string;

  // About
  aboutTitle: string;
  aboutSubtitle: string;
  aboutDescription: string;
  aboutMission: string;

  // Services
  servicesTitle: string;
  servicesSubtitle: string;
  contractingTitle: string;
  contractingDesc: string;
  tradingTitle: string;
  tradingDesc: string;
  equipmentTitle: string;
  equipmentDesc: string;
  workforceTitle: string;
  workforceDesc: string;

  // Vehicle Rental
  vehicleTitle: string;
  vehicleSubtitle: string;
  vehicleDescription: string;
  fleetTitle: string;
  fleetDesc: string;
  flexibleTitle: string;
  flexibleDesc: string;
  supportTitle: string;
  supportDesc: string;

  // Contact
  contactTitle: string;
  contactSubtitle: string;
  contactDescription: string;
  phone: string;
  email: string;
  address: string;
  location: string;

  // Legal Documents
  documentsTitle: string;
  documentsSubtitle: string;
  commercialRegTitle: string;
  commercialRegDesc: string;
  vatRegTitle: string;
  vatRegDesc: string;
  chamberTitle: string;
  chamberDesc: string;
  bankDetailsTitle: string;
  bankDetailsDesc: string;
  bankName: string;
  accountNumber: string;
  ibanNumber: string;
  enterPinToView: string;
  unlock: string;
  invalidPin: string;
  copied: string;
  success: string;
  error: string;
  nationalAddressTitle: string;
  nationalAddressDesc: string;
  downloadDocument: string;
  viewDocument: string;

  // Major Clients
  majorClientsTitle: string;
  majorClientsSubtitle: string;
  trustedPartner: string;
  notableProjects: string;
  notableProjectsSubtitle: string;

  // HSE Policy
  hseTitle: string;
  hseSubtitle: string;
  hseIntro: string;
  safetyCommitment: string;
  safetyText: string;
  environmentalPolicy: string;
  environmentalText: string;
  complianceTitle: string;
  complianceText: string;

  // Footer
  footerTagline: string;
  quickLinks: string;
  ourServices: string;
  followUs: string;
  allRights: string;
  formerlyKnown: string;
  privacyPolicy: string;
  termsOfService: string;
  qualityStandards: string;
  emergencySupport: string;

  // Contact Form
  firstName: string;
  lastName: string;
  emailAddress: string;
  companyName: string;
  phoneNumber: string;
  projectRequirements: string;
  sendMessage: string;
  sending: string;
  thankYouMessage: string;
  errorMessage: string;
  contactInformation: string;
  contactInfoDesc: string;
  businessHours: string;
  businessHoursTime: string;
  emergencyAvailable: string;
  availableEmergencies: string;

  // About Stats
  yearsExperience: string;
  projectsCompleted: string;
  supportAvailable: string;

  // Service Features
  projectExecution: string;
  buildSupport: string;
  smartMaintenance: string;
  qualityAssurance: string;
  materialSupply: string;
  qualityMaterials: string;
  timelyDelivery: string;
  competitivePricing: string;
  extensiveFleet: string;
  topQualityGear: string;
  rapidDelivery: string;
  flexibleTerms: string;
  expertPersonnel: string;
  skilledWorkers: string;
  professionalSupport: string;
  reliableService: string;

  // Vehicle Types
  heavyTrucks: string;
  heavyTrucksDesc: string;
  constructionVehicles: string;
  constructionVehiclesDesc: string;
  commercialVehicles: string;
  commercialVehiclesDesc: string;
  specializedEquipment: string;
  specializedEquipmentDesc: string;
  wellMaintained: string;
  maintenanceIncluded: string;
  excavators: string;
  bulldozers: string;
  cranes: string;
  materialHandlers: string;
  pickupTrucks: string;
  vans: string;
  suvs: string;
  sedans: string;
  customSolutions: string;
  technicalSupport: string;
  emergencyService: string;
}

export type Translations = Record<Language, TranslationKeys>;

// Contact Form Types
export interface ContactFormData {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  phone: string;
  message: string;
}

// Legal Document Types
export interface LegalDocument {
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  file: string;
  color: string;
  bgColor: string;
}

// Organization Chart Types
export interface TeamMember {
  name: string;
  position: string;
  level: number;
  isManager?: boolean;
}

// Context Types
export interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  isRTL: boolean;
}

// Utility Types for Enhanced Security
export type SafeString = string & { readonly __brand: unique symbol };
export type ValidatedEmail = string & { readonly __emailBrand: unique symbol };
export type ValidatedPhone = string & { readonly __phoneBrand: unique symbol };

// Form validation helpers
export const createSafeString = (input: unknown): SafeString | null => {
  if (typeof input === 'string' && input.trim().length > 0) {
    return input.trim() as SafeString;
  }
  return null;
};

export const validateEmail = (email: string): ValidatedEmail | null => {
  // RFC 5322 compliant regex (simplified but robust)
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  
  const trimmed = email.trim();
  if (!emailRegex.test(trimmed)) return null;
  if (trimmed.length > 254) return null; // RFC 5321 limit
  if (trimmed.includes('..')) return null; // No consecutive dots
  
  const [local, domain] = trimmed.split('@');
  if (local.length > 64) return null; // RFC 5321 limit
  if (domain.length > 253) return null;
  
  return trimmed as ValidatedEmail;
};

export const validatePhone = (phone: string): ValidatedPhone | null => {
  const trimmed = phone.trim();
  // Remove spaces, hyphens, parentheses for validation
  const cleaned = trimmed.replace(/[\s()\-]/g, '');
  
  // Saudi Arabia phone number patterns
  // International: +966 5XXXXXXXX (mobile) or +966 [1-4]XXXXXXX (landline)
  // Local: 05XXXXXXXX or 0[1-4]XXXXXXX
  const saudiMobileIntl = /^\+9665\d{8}$/;
  const saudiMobileLocal = /^05\d{8}$/;
  const saudiLandlineIntl = /^\+966[1-4]\d{7}$/;
  const saudiLandlineLocal = /^0[1-4]\d{7}$/;
  
  // Also accept general international format for flexibility
  const generalIntl = /^\+\d{10,15}$/;
  
  if (saudiMobileIntl.test(cleaned) || saudiMobileLocal.test(cleaned) ||
      saudiLandlineIntl.test(cleaned) || saudiLandlineLocal.test(cleaned) ||
      generalIntl.test(cleaned)) {
    return trimmed as ValidatedPhone;
  }
  
  return null;
};